import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, Home, Mail, Send, Inbox, Copy, Download, Upload, Edit3, X,
  RefreshCw, Undo2, Check, Info, FileText, Search,
} from 'lucide-react';
import { toast } from 'sonner';

import API_URL from '@/config/api';
import AuthContext from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { formatApiError } from '@/utils/apiError';

/** User-approved pill order (matches EmailSamples registry buckets). */
const FIXED_CATEGORY_ORDER = ['Training Courses', 'Customer Leads', 'Ox Approved Programme', 'Marketing & Upsells', 'Orders & Quotes', 'Stock & Suppliers', 'Ambassador Referrals', 'Accounts & Logins'];
const slug = (c) => c.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');

/**
 * Split-pane admin editor for the 19 transactional email templates.
 * Left: filterable list of samples grouped by category.
 * Right: subject + body source editor with a live iframe preview, copy /
 * download / upload / send-test / publish / revert.
 *
 * Publishing writes to `email_sample_overrides` (Mongo) — until we wire the
 * override into every outgoing email path, published copy only affects
 * SAMPLE sends. UI copy is explicit about this so admins aren't surprised.
 */
export default function EmailSamples() {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const canPublish = user && ['admin', 'manager'].includes(user.role);

  const [samples, setSamples] = useState([]);
  const [to, setTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [sendingKey, setSendingKey] = useState(null);
  const [activeCategory, setActiveCategory] = useState('All');
  const [query, setQuery] = useState('');
  const searchRef = useRef(null);
  const [searchParams] = useSearchParams();
  const [selectedKey, setSelectedKey] = useState(searchParams.get('key') || null);

  // Detail-pane state — pulled from /api/staff/email-samples/{key}/source
  const [detail, setDetail] = useState(null);           // full payload
  const [detailLoading, setDetailLoading] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draftSubject, setDraftSubject] = useState('');
  const [draftBody, setDraftBody] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewBody, setPreviewBody] = useState('');
  const [publishing, setPublishing] = useState(false);
  const [reverting, setReverting] = useState(false);
  const [copyLabel, setCopyLabel] = useState('Copy HTML');

  const iframeRef = useRef(null);
  const fileInputRef = useRef(null);
  const renderTimerRef = useRef(null);

  // ── list load ─────────────────────────────────────────────────────
  const loadList = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/staff/email-samples`, { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Failed to load samples');
      }
      const d = await res.json();
      setSamples(d.samples || []);
      setTo(d.default_to || user?.email || '');
    } catch (e) {
      toast.error(e.message);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => { loadList(); }, [loadList]);

  const grouped = useMemo(() => {
    const m = {};
    samples.forEach(s => { (m[s.category] ||= []).push(s); });
    return m;
  }, [samples]);

  const categoryPills = useMemo(() => {
    const seen = Object.keys(grouped);
    const ordered = [
      ...FIXED_CATEGORY_ORDER.filter(c => seen.includes(c)),
      ...seen.filter(c => !FIXED_CATEGORY_ORDER.includes(c)).sort(),
    ];
    return ['All', ...ordered];
  }, [grouped]);

  const filteredSamples = useMemo(() => {
    const rank = (c) => { const i = FIXED_CATEGORY_ORDER.indexOf(c); return i === -1 ? 99 : i; };
    const q = query.trim().toLowerCase();
    let list = activeCategory === 'All' || q ? samples : samples.filter(s => s.category === activeCategory);
    if (q) list = list.filter(s => `${s.label} ${s.description || ''} ${s.category} ${s.key}`.toLowerCase().includes(q));
    return [...list].sort((a, b) => rank(a.category) - rank(b.category));
  }, [samples, activeCategory, query]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === '/' && !['INPUT', 'TEXTAREA'].includes(document.activeElement?.tagName)) { e.preventDefault(); searchRef.current?.focus(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const countFor = (cat) => cat === 'All' ? samples.length : (grouped[cat]?.length || 0);

  // ── detail load ───────────────────────────────────────────────────
  const loadDetail = useCallback(async (key) => {
    setDetailLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/email-samples/${key}/source`, { credentials: 'include' });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Failed to load template');
      }
      const d = await res.json();
      setDetail(d);
      setDraftSubject(d.subject_source);
      setDraftBody(d.body_source);
      setPreviewSubject(d.subject_rendered);
      setPreviewBody(d.body_rendered);
      setEditing(false);
    } catch (e) {
      toast.error(e.message);
    } finally {
      setDetailLoading(false);
    }
  }, []);

  useEffect(() => {
    if (selectedKey) loadDetail(selectedKey);
  }, [selectedKey, loadDetail]);

  // ── live render (debounced) ───────────────────────────────────────
  const renderDraft = useCallback(async (subject, body) => {
    if (!selectedKey) return;
    try {
      const res = await fetch(`${API_URL}/api/staff/email-samples/${selectedKey}/render`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, body }),
      });
      if (!res.ok) return;
      const d = await res.json();
      setPreviewSubject(d.subject_rendered);
      setPreviewBody(d.body_rendered);
    } catch { /* ignore transient render errors */ }
  }, [selectedKey]);

  useEffect(() => {
    if (!editing) return;
    clearTimeout(renderTimerRef.current);
    renderTimerRef.current = setTimeout(() => {
      renderDraft(draftSubject, draftBody);
    }, 350);
    return () => clearTimeout(renderTimerRef.current);
  }, [editing, draftSubject, draftBody, renderDraft]);

  // ── iframe paint ──────────────────────────────────────────────────
  const paintIframe = useCallback((html) => {
    const frame = iframeRef.current;
    if (!frame) return;
    const doc = frame.contentDocument;
    if (!doc) return;
    doc.open();
    doc.write(`<!doctype html><html><head><meta charset="utf-8"></head><body style="margin:0;background:#f8fafc;padding:16px;">${html || ''}</body></html>`);
    doc.close();
  }, []);

  useEffect(() => {
    if (!detail) return;
    requestAnimationFrame(() => paintIframe(previewBody));
  }, [detail, previewBody, paintIframe]);

  // ── actions ───────────────────────────────────────────────────────
  const sendSample = async () => {
    if (!to || !to.includes('@')) { toast.error('Enter a valid email address to send the sample to.'); return; }
    if (!selectedKey) return;
    setSendingKey(selectedKey);
    try {
      const body = { template_key: selectedKey, to };
      if (editing) {
        body.subject_override = draftSubject;
        body.body_override = draftBody;
      }
      const res = await fetch(`${API_URL}/api/staff/email-samples/send`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => null);
        throw new Error(formatApiError(err) || `Send failed (${res.status})`);
      }
      const d = await res.json();
      toast.success(`${d.label} → ${d.sent_to}${d.scratchpad ? ' (edit copy)' : ''}`);
    } catch (e) { toast.error(e.message); }
    finally { setSendingKey(null); }
  };

  const copyHtml = async () => {
    const html = editing ? draftBody : (detail?.body_source || '');
    if (!html) return;
    try {
      if (navigator.clipboard?.writeText) await navigator.clipboard.writeText(html);
      else {
        const ta = document.createElement('textarea');
        ta.value = html; ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta); ta.select();
        document.execCommand('copy'); document.body.removeChild(ta);
      }
      setCopyLabel('Copied');
      setTimeout(() => setCopyLabel('Copy HTML'), 1500);
      toast.success('HTML copied to clipboard');
    } catch { toast.error('Copy failed — browser blocked clipboard access.'); }
  };

  const downloadHtml = () => {
    const html = editing ? draftBody : (detail?.body_source || '');
    if (!html) return;
    const filename = `${selectedKey || 'email-sample'}.html`;
    const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename;
    document.body.appendChild(a); a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success(`Downloaded ${filename}`);
  };

  const uploadHtml = (file) => {
    if (!file) return;
    if (!/\.(html?|txt)$/i.test(file.name)) {
      toast.error('Only .html / .htm / .txt files are supported.');
      return;
    }
    if (file.size > 500_000) {
      toast.error('File is over 500 KB — trim it down first.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const text = String(reader.result || '');
      setEditing(true);
      setDraftBody(text);
      toast.success(`Loaded ${file.name} into the editor.`);
    };
    reader.onerror = () => toast.error('Could not read file.');
    reader.readAsText(file);
  };

  const publish = async () => {
    if (!selectedKey || !canPublish) return;
    const wired = detail?.production_wired;
    const msg = wired
      ? 'Publish these edits as the new default? Real production emails (welcome, invoice, quote, etc.) will use this copy from the next send.'
      : 'Publish these edits as the new default? This template isn\'t wired to a real production send yet — for now, only "Send test" will use this copy. Continue anyway?';
    if (!window.confirm(msg)) return;
    setPublishing(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/email-samples/${selectedKey}/publish`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject: draftSubject, body: draftBody }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Publish failed');
      }
      toast.success('Published — this template now uses your edits.');
      setEditing(false);
      await loadDetail(selectedKey);
      await loadList();
    } catch (e) { toast.error(e.message); }
    finally { setPublishing(false); }
  };

  const revert = async () => {
    if (!selectedKey || !canPublish || !detail?.is_overridden) return;
    if (!window.confirm('Revert to the code default and delete your saved edits? This cannot be undone.')) return;
    setReverting(true);
    try {
      const res = await fetch(`${API_URL}/api/staff/email-samples/${selectedKey}/override`, {
        method: 'DELETE', credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatApiError(err) || 'Revert failed');
      }
      toast.success('Reverted to code default.');
      setEditing(false);
      await loadDetail(selectedKey);
      await loadList();
    } catch (e) { toast.error(e.message); }
    finally { setReverting(false); }
  };

  const cancelEdit = () => {
    if (!detail) return;
    setDraftSubject(detail.subject_source);
    setDraftBody(detail.body_source);
    setPreviewSubject(detail.subject_rendered);
    setPreviewBody(detail.body_rendered);
    setEditing(false);
  };

  const insertMergeField = (field) => {
    setDraftBody(prev => prev + `{{${field}}}`);
  };

  const isDirty = editing && detail && (draftSubject !== detail.subject_source || draftBody !== detail.body_source);

  // ─────────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <div className="border-b sticky top-0 z-30 shadow-sm bg-slate-900 text-white shrink-0">
        <div className="max-w-[1400px] mx-auto px-3 sm:px-4 py-3 flex items-center gap-2 sm:gap-3">
          <button onClick={() => navigate(-1)} className="text-white/80 hover:text-white shrink-0" data-testid="email-samples-back" aria-label="Back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Link to="/" className="text-white/80 hover:text-white shrink-0 hidden sm:inline-flex" aria-label="Home">
            <Home className="w-5 h-5" />
          </Link>
          <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <Mail className="w-4 h-4" />
          </div>
          <h1 className="text-base sm:text-lg font-bold truncate flex-1 min-w-0">Email templates</h1>
          <Link
            to="/staff/diagnostics/email"
            className="text-xs text-white/80 hover:text-white underline shrink-0 inline-flex items-center gap-1"
            data-testid="email-samples-open-delivery"
          >
            <Inbox className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Delivery log</span>
          </Link>
        </div>
      </div>

      <div className="flex-1 max-w-[1400px] w-full mx-auto px-3 sm:px-4 py-4 grid grid-cols-1 lg:grid-cols-[340px_1fr] gap-4 min-h-0">
        {/* LEFT · list + pills + send-to */}
        <div className="flex flex-col min-h-0 gap-3">
          <div className="relative shrink-0">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" />
            <Input
              ref={searchRef} value={query} onChange={(e) => setQuery(e.target.value)}
              placeholder="Search templates… (press / to focus)" className="pl-9 pr-8 h-10 bg-white"
              aria-label="Search templates" data-testid="email-samples-search"
            />
            {query && (
              <button type="button" onClick={() => setQuery('')} className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-gray-400 hover:text-gray-700" aria-label="Clear search" data-testid="email-samples-search-clear">
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          <Card className="p-3 shrink-0">
            <Label htmlFor="samples-to" className="text-xs font-semibold text-gray-600">Send test copies to</Label>
            <Input
              id="samples-to" type="email" value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="you@oxresin.co.uk" className="mt-1 bg-white h-9"
              data-testid="email-samples-to-input"
            />
          </Card>

          <div
            className="flex flex-wrap gap-1.5 shrink-0"
            data-testid="email-samples-category-bar"
          >
            {categoryPills.map(cat => {
              const active = !query && activeCategory === cat;
              return (
                <button
                  key={cat}
                  onClick={() => { setQuery(''); setActiveCategory(cat); }}
                  data-testid={`email-samples-filter-${slug(cat)}`}
                  className={
                    'shrink-0 rounded-full px-3 py-1 text-xs font-semibold border transition ' +
                    (active
                      ? 'bg-slate-900 text-white border-slate-900'
                      : 'bg-white text-gray-700 border-gray-300 hover:border-gray-500')
                  }
                >
                  {cat}
                  <span className={'ml-1.5 inline-flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full text-[10px] font-bold ' + (active ? 'bg-white/20' : 'bg-gray-100 text-gray-600')}>
                    {countFor(cat)}
                  </span>
                </button>
              );
            })}
          </div>

          <Card className="flex-1 overflow-auto min-h-[200px]" data-testid="email-samples-list">
            {loading ? (
              <div className="p-8 text-center text-gray-500 italic text-sm">Loading…</div>
            ) : filteredSamples.length === 0 ? (
              <div className="p-8 text-center text-gray-500 italic text-sm" data-testid="email-samples-empty">{query ? <>No templates match &quot;{query}&quot;.</> : 'No templates in this bucket.'}</div>
            ) : (
              <ul className="divide-y divide-gray-100">
                {filteredSamples.map(s => {
                  const isSelected = s.key === selectedKey;
                  return (
                    <li key={s.key}>
                      <button
                        onClick={() => setSelectedKey(s.key)}
                        data-testid={`email-samples-select-${s.key}`}
                        className={
                          'w-full text-left px-4 py-3 hover:bg-slate-50 transition flex flex-col gap-0.5 ' +
                          (isSelected ? 'bg-slate-100 border-l-4 border-slate-900' : 'border-l-4 border-transparent')
                        }
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-semibold text-gray-900 flex-1 min-w-0 truncate">{s.label}</span>
                          {s.is_overridden && (
                            <span
                              data-testid={`email-samples-edited-${s.key}`}
                              className="shrink-0 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 uppercase tracking-wider"
                              title="This template has been edited by staff — using published copy"
                            >
                              Edited
                            </span>
                          )}
                        </div>
                        <span className="text-[11px] text-gray-500 uppercase tracking-wider">{s.category}</span>
                        <span className="text-xs text-gray-600 line-clamp-2">{s.description}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </Card>
        </div>

        {/* RIGHT · detail: subject + editor + preview */}
        <div className="min-h-0 flex flex-col">
          {!selectedKey ? (
            <Card className="flex-1 flex items-center justify-center p-12 text-center text-gray-500">
              <div>
                <FileText className="w-10 h-10 mx-auto text-gray-300 mb-3" />
                <p className="font-semibold text-gray-700">Pick a template on the left</p>
                <p className="text-sm mt-1 max-w-md">Every transactional email the system fires is here. Preview it, tweak the copy, or send yourself a test.</p>
              </div>
            </Card>
          ) : (
            <Card className="flex-1 flex flex-col overflow-hidden min-h-0" data-testid="email-samples-detail">
              {detailLoading || !detail ? (
                <div className="flex-1 flex items-center justify-center text-gray-500 italic">Loading template…</div>
              ) : (
                <>
                  {/* Detail header */}
                  <div className="px-4 py-3 border-b bg-gray-50 flex items-start gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{detail.category}</span>
                        {detail.is_overridden && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 uppercase tracking-wider" title={`Edited by ${detail.override_meta?.updated_by_name || 'staff'}`}>
                            Edited
                          </span>
                        )}
                        {!detail.editable && (
                          <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-slate-200 text-slate-700 uppercase tracking-wider" title="Dynamically generated at send time">
                            Read-only
                          </span>
                        )}
                        {detail.editable && detail.production_wired && (
                          <span
                            className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-800 uppercase tracking-wider"
                            title="Publishing an override here changes what real recipients receive on the next send"
                            data-testid="email-samples-live-badge"
                          >
                            Live
                          </span>
                        )}
                        {detail.editable && !detail.production_wired && (
                          <span
                            className="text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-800 uppercase tracking-wider"
                            title="This template isn't wired to a real production send yet — publishing only affects Send test."
                            data-testid="email-samples-preview-only-badge"
                          >
                            Preview only
                          </span>
                        )}
                      </div>
                      <div className="font-bold text-gray-900 truncate">{detail.label}</div>
                      <div className="text-xs text-gray-500 mt-0.5">{detail.description}</div>
                    </div>
                    <div className="flex gap-1.5 shrink-0 flex-wrap">
                      {!editing && detail.editable && (
                        <Button size="sm" variant="outline" onClick={() => setEditing(true)} data-testid="email-samples-edit-btn">
                          <Edit3 className="w-3.5 h-3.5 mr-1.5" />Edit
                        </Button>
                      )}
                      {editing && (
                        <>
                          <Button size="sm" variant="outline" onClick={cancelEdit} data-testid="email-samples-cancel-edit">
                            <X className="w-3.5 h-3.5 mr-1.5" />Cancel
                          </Button>
                          {canPublish && (
                            <Button size="sm" onClick={publish} disabled={publishing || !isDirty} data-testid="email-samples-publish-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
                              <Check className={`w-3.5 h-3.5 mr-1.5 ${publishing ? 'animate-pulse' : ''}`} />
                              {publishing ? 'Publishing…' : 'Publish as default'}
                            </Button>
                          )}
                        </>
                      )}
                      {!editing && detail.is_overridden && canPublish && (
                        <Button size="sm" variant="outline" onClick={revert} disabled={reverting} data-testid="email-samples-revert-btn">
                          <Undo2 className={`w-3.5 h-3.5 mr-1.5 ${reverting ? 'animate-spin' : ''}`} />
                          {reverting ? 'Reverting…' : 'Revert'}
                        </Button>
                      )}
                    </div>
                  </div>

                  {detail.dynamic_note && (
                    <div className="px-4 py-2 border-b bg-blue-50 text-xs text-blue-900 flex items-start gap-2">
                      <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
                      <span>{detail.dynamic_note}</span>
                    </div>
                  )}

                  {/* Editor + preview split */}
                  <div className="flex-1 grid grid-cols-1 xl:grid-cols-2 gap-0 min-h-0 overflow-hidden">
                    {/* LEFT · source */}
                    <div className="flex flex-col min-h-0 border-r">
                      <div className="p-3 border-b bg-slate-50">
                        <Label className="text-xs font-semibold text-gray-600">Subject</Label>
                        <Input
                          value={editing ? draftSubject : detail.subject_source}
                          onChange={(e) => setDraftSubject(e.target.value)}
                          disabled={!editing}
                          className="mt-1 bg-white h-9 font-mono text-sm"
                          data-testid="email-samples-subject-input"
                        />
                        <div className="text-[10px] text-gray-500 mt-1">
                          Rendered: <span className="font-mono">{previewSubject || '(empty)'}</span>
                        </div>
                      </div>
                      <div className="flex-1 flex flex-col min-h-0 p-3">
                        <div className="flex items-center justify-between mb-1">
                          <Label className="text-xs font-semibold text-gray-600">Body HTML</Label>
                          <div className="flex gap-1">
                            <Button size="sm" variant="outline" onClick={copyHtml} disabled={!detail} data-testid="email-samples-copy-html" className="h-7 text-xs">
                              <Copy className="w-3 h-3 mr-1" />{copyLabel}
                            </Button>
                            <Button size="sm" variant="outline" onClick={downloadHtml} disabled={!detail} data-testid="email-samples-download-html" className="h-7 text-xs">
                              <Download className="w-3 h-3 mr-1" />Download
                            </Button>
                            {editing && detail.editable && (
                              <>
                                <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} data-testid="email-samples-upload-html" className="h-7 text-xs">
                                  <Upload className="w-3 h-3 mr-1" />Upload
                                </Button>
                                <input
                                  ref={fileInputRef} type="file" accept=".html,.htm,.txt" className="hidden"
                                  onChange={(e) => { uploadHtml(e.target.files?.[0]); e.target.value = ''; }}
                                  data-testid="email-samples-upload-input"
                                />
                              </>
                            )}
                          </div>
                        </div>
                        <Textarea
                          value={editing ? draftBody : detail.body_source}
                          onChange={(e) => setDraftBody(e.target.value)}
                          disabled={!editing}
                          spellCheck={false}
                          className="flex-1 min-h-[220px] font-mono text-xs resize-none bg-white"
                          data-testid="email-samples-body-textarea"
                          placeholder="<p>Your HTML here — use {{merge_fields}} for dynamic values.</p>"
                        />
                        {detail.merge_fields?.length > 0 && (
                          <div className="mt-2 flex items-start gap-2 flex-wrap">
                            <span className="text-[10px] font-semibold uppercase text-gray-500 mt-1">Merge fields:</span>
                            {detail.merge_fields.map(f => (
                              <button
                                key={f}
                                type="button"
                                onClick={() => editing && insertMergeField(f)}
                                disabled={!editing}
                                title={editing ? `Insert {{${f}}} into body` : `Available: {{${f}}}`}
                                data-testid={`email-samples-merge-${f}`}
                                className={
                                  'text-[10px] font-mono px-1.5 py-0.5 rounded border bg-white ' +
                                  (editing
                                    ? 'text-slate-800 border-slate-300 hover:bg-slate-100 cursor-pointer'
                                    : 'text-gray-500 border-gray-200 cursor-default')
                                }
                              >
                                {'{{'}{f}{'}}'}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* RIGHT · live preview */}
                    <div className="flex flex-col min-h-0 bg-slate-100">
                      <div className="p-2 border-b bg-slate-50 text-[11px] text-gray-500 flex items-center gap-2 justify-between">
                        <span>Live preview</span>
                        <Button size="sm" variant="outline" onClick={() => loadDetail(selectedKey)} className="h-6 text-[10px]" data-testid="email-samples-refresh-preview">
                          <RefreshCw className="w-3 h-3 mr-1" />Refresh
                        </Button>
                      </div>
                      <div className="flex-1 min-h-[300px]">
                        <iframe
                          ref={iframeRef} title="Email preview" sandbox="allow-same-origin"
                          className="w-full h-full block bg-white"
                          style={{ minHeight: 300 }}
                          data-testid="email-samples-preview-iframe"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Footer · send test */}
                  <div className="px-4 py-3 border-t bg-white flex items-center gap-2 flex-wrap">
                    <Button
                      onClick={sendSample}
                      disabled={sendingKey === selectedKey || !to}
                      data-testid={`email-samples-send-${selectedKey}`}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    >
                      <Send className={`w-4 h-4 mr-1.5 ${sendingKey === selectedKey ? 'animate-pulse' : ''}`} />
                      {sendingKey === selectedKey
                        ? 'Sending…'
                        : editing ? 'Send edit as test' : 'Send test'}
                    </Button>
                    {editing && !canPublish && (
                      <span className="text-[11px] text-gray-500 italic">You can send this edit as a test, but only admins/managers can publish it as the new default.</span>
                    )}
                  </div>
                </>
              )}
            </Card>
          )}
        </div>
      </div>
    </div>
  );
}
